import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiModalService } from '@siemens/element-ng/modal';
import { take } from 'rxjs';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import { Track } from 'src/app/main/shared/DataTypes';
import { BrouterMapComponent } from 'src/app/main/shared/brouter-map/brouter-map.component';
import { Waypoint } from 'src/app/main/shared/brouter-map/models/brouter-map.model';
import { createWaypoint } from 'src/app/main/shared/brouter-map/services/route-editor.service';

/**
 * TrackMapComponent
 *
 * A standalone component responsible for displaying track routes on a BRouter map.
 * This component handles:
 * - Opening and managing the BRouter map modal
 * - Rendering the stored route read-only
 * - Fullscreen toggle functionality
 * - Empty state when no route data is available
 */
@Component({
  selector: 'app-track-map',
  imports: [CommonModule, TranslateModule, SiEmptyStateModule, BrouterMapComponent],
  standalone: true,
  templateUrl: './track-map.component.html',
  styleUrl: './track-map.component.scss'
})
export class TrackMapComponent implements OnDestroy {
  private readonly modalService = inject(SiModalService);

  // Template reference
  readonly trackMapModalTemplate = viewChild<TemplateRef<any>>('trackMapModal');

  // Inputs
  readonly track = input.required<Track>();
  readonly editRequested = output<Waypoint[]>();

  // State
  readonly routeWaypoints = signal<Waypoint[] | null>(null);
  readonly isFullscreen = signal<boolean>(false);

  private currentModalRef: any = null;

  /**
   * Opens the map modal with the track's stored route.
   * The map fits the view to the route itself, so no zoom heuristic is needed.
   */
  openModal(): void {
    // Close any existing modal before opening a new one
    if (this.currentModalRef) {
      this.currentModalRef.hide();
    }

    const trackData = this.track();

    if (!trackData?.trackDataQuery) {
      console.warn('No BRouter query data available for this track');
      return;
    }

    const waypoints = trackData.trackDataQuery.waypoints;

    if (!waypoints || waypoints.length < 2) {
      console.warn('Insufficient waypoints in track data query');
      return;
    }

    this.routeWaypoints.set(waypoints.map(wp => createWaypoint({ lat: wp.lat, lng: wp.lng })));

    // Open modal
    const template = this.trackMapModalTemplate();
    if (template) {
      this.currentModalRef = this.modalService.show(template, {
        ...STANDARD_MODAL_CONFIG,
        class: 'map-modal modal-dialog-centered'
      });

      // Centralized cleanup for any dismiss path (close button, ESC key, backdrop click)
      this.currentModalRef.hidden?.pipe(take(1)).subscribe(() => {
        this.isFullscreen.set(false);
        this.routeWaypoints.set(null);
        this.currentModalRef = null;
      });
    }
  }

  /**
   * Closes the BRouter map modal.
   * State cleanup is handled centrally by the onHidden subscriber.
   */
  closeModal(): void {
    this.currentModalRef?.hide();
  }

  /**
   * Lifecycle cleanup: ensure modal is closed when component is destroyed
   * This prevents orphaned modals from appearing when the component is recreated
   * (e.g., during export or table refresh)
   */
  ngOnDestroy(): void {
    if (this.currentModalRef) {
      this.currentModalRef.hide();
    }
  }

  /**
   * Toggles fullscreen mode for the BRouter map modal
   */
  toggleFullscreen(): void {
    this.isFullscreen.update(value => !value);
    const dialog = document.querySelector('.map-modal');
    dialog?.classList.toggle('map-modal--fullscreen', this.isFullscreen());
  }

  openEditTool(waypoints: Waypoint[]): void {
    this.editRequested.emit(waypoints);
    this.closeModal();
  }
}
